import React, { useEffect, useState } from "react";

import { useFormikContext } from "formik";
import * as yup from "yup";

import { useRecoilValue } from "recoil";
import { userCredentials } from "recoilState/GlobalState";

import { FormikSelectInput } from "components/UI/Inputs/FormikInputs";
import { estilos } from "components/utils/mapa/BloqueUbicacion";

import { RNHAreaGet } from "services/services-mia/programa-salud/estructuras/RNHAreaGet";
import { RNHNivelGet } from "services/services-mia/programa-salud/estructuras/RNHNivelGet";
import { RNHZonaGet } from "services/services-mia/programa-salud/estructuras/RNHZonaGet";

import { CamposIdentificacion } from "pages/programa-salud/estructuras/establecimientos/EstablecimientoFormulario";

/**
 * Lo propio de un hospital: nivel, zona, area y cabecera. Es lo unico que lo separa de las
 * otras cuatro entidades, que usan el formulario de establecimiento tal cual; entra por el
 * prop `camposExtra`.
 *
 * Reparto 8+4 como el resto del formulario, para que todo corte en la misma columna: las 8
 * para nivel y area, que son de texto largo.
 */

/**
 * Cabecera: el back la guarda en un caracter. Son las dos unicas posibles.
 *
 * No hay "ninguna": todo hospital es cabecera de algo. Y ser cabecera de zona implica
 * serlo del area, por eso la segunda opcion son las dos juntas y no "Zona" a secas.
 *
 * El codigo de la segunda es `Z`: es el que la base usa hoy para "cabecera de zona" (el
 * hospital de Cinco Saltos lo tiene y es el que figura en `rnhEstablecimientoCabeceraZona`
 * de su zona). Se habia arrancado con `B`, que era su nombre en el esquema viejo, y por eso
 * el select del modal de modificacion abria vacio: el valor guardado no estaba entre las
 * opciones y MUI no tiene que seleccionar.
 */
const CABECERA_OPCIONES = [
    { value: "A", nombre: "Área" },
    { value: "Z", nombre: "Zona y Área" },
];

/**
 * Las cabeceras que no tienen sentido sin un area elegida: las dos, porque "Zona y Area"
 * tambien lo es del area.
 */
const CABECERAS_CON_AREA = ["A", "Z"];

/**
 * Que cabeceras se pueden elegir con la zona ya seleccionada.
 *
 * Una zona tiene UNA sola cabecera, asi que "Zona y Area" se ofrece solo si esta libre o
 * si ya la ocupa este mismo hospital —el caso de la modificacion, donde hay que seguir
 * mostrando lo que tiene guardado—. "Area" se ofrece siempre: el select de areas ya viene
 * filtrado por el back a las que pueden tener cabecera.
 *
 * Se compara por ID y no por el nombre del hospital: dos hospitales pueden llamarse
 * parecido, y el centinela de texto ("Sin asignar") es justo lo que se rompe en silencio
 * si el back lo cambia.
 *
 * Mientras `rnhEstablecimientoCabeceraZonaId` no llegue, queda `undefined` y la zona se
 * lee como libre. Es fallar ABIERTO a proposito: es preferible ofrecer de mas y que el
 * back rechace, a bloquear el alta de un hospital por un campo que todavia no viaja.
 */
const cabecerasDeLaZona = (zonas, zonaId, hospitalId) => {
    const zona = zonas.find((z) => String(z.rnhZonaId) === String(zonaId));
    if (!zona) return [];

    const cabeceraDeLaZona = zona.rnhEstablecimientoCabeceraZonaId;
    const zonaDisponible =
        cabeceraDeLaZona == null ||
        String(cabeceraDeLaZona) === String(hospitalId);

    return zonaDisponible ? ["A", "Z"] : ["A"];
};

/**
 * Cod de una zona a partir de su id.
 *
 * RNHAreaGet filtra por `rnhZonaCod`, pero esta vista trabaja con ids de zona (el mundo de los
 * establecimientos quedo en ids, ver claves-cod-vs-id.md). Mandarle `zonaId` no era un filtro
 * invalido: era NINGUN filtro, asi que el servicio devolvia TODAS las areas de la provincia y
 * el select mostraba las de cualquier zona. El catalogo de zonas trae los dos campos, asi que
 * se traduce aca.
 */
const codDeZona = (zonas, zonaId) =>
    zonas.find((z) => String(z.rnhZonaId) === String(zonaId))?.rnhZonaCod || null;

/**
 * Anchos del domicilio propios del hospital: localidad y codigo postal comparten fila.
 *
 * Con cuatro campos extra arriba, el hospital tiene dos filas mas que el resto y esa fila
 * ahorrada es la que evita que el modal termine con scroll. Se compensa con size="xl".
 */
export const ANCHOS_HOSPITAL = {
    calle: 8,
    numero: 4,
    localidad: 8,
    cpa: 4,
};

/** Los campos del hospital, vacios, para los initialValues de un alta. */
export const valoresHospitalVacios = () => ({
    nivelId: "",
    zonaId: "",
    areaId: "",
    cabecera: "",
});

/** Las reglas de esos campos, para sumar al schema comun del establecimiento. */
export const reglasHospital = {
    nivelId: yup.string().required("Debe seleccionar un nivel"),
    zonaId: yup.string().required("Debe seleccionar una zona"),
    areaId: yup.string().required("Debe seleccionar un área"),
    // Obligatoria desde que se saco "Ninguna": todo hospital es cabecera de algo.
    cabecera: yup.string().required("Debe seleccionar una"),
};

/**
 * Los dos catalogos fijos los carga el MODAL y no este componente: la modificacion los
 * necesita ANTES de armar los valores iniciales, porque el establecimiento devuelve el
 * nivel como Cod ("Nivel III") y el select trabaja con ids.
 *
 * Las areas no estan aca porque dependen de la zona elegida y las maneja el componente.
 */
export const useCatalogosHospital = () => {
    const credentials = useRecoilValue(userCredentials);
    const [catalogos, setCatalogos] = useState({
        niveles: [],
        zonas: [],
        cargando: true,
    });

    useEffect(() => {
        let vigente = true;
        (async () => {
            const [niveles, zonas] = await Promise.all([
                RNHNivelGet(credentials),
                RNHZonaGet(credentials),
            ]);
            if (vigente) {
                setCatalogos({
                    niveles: niveles.data ?? [],
                    zonas: zonas.data ?? [],
                    cargando: false,
                });
            }
        })();

        return () => {
            vigente = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return catalogos;
};

export default function CamposHospital({ niveles, zonas, hospitalId }) {
    const credentials = useRecoilValue(userCredentials);
    const { values, setFieldValue } = useFormikContext();

    const [areas, setAreas] = useState([]);
    const [areasCargando, setAreasCargando] = useState(false);

    useEffect(() => {
        const zonaId = values.zonaId;

        if (!zonaId) {
            setAreas([]);
            return undefined;
        }

        // Sin el catalogo de zonas no se puede traducir el id a cod. Se reintenta cuando
        // llegue, porque el efecto vuelve a correr con zonas en las dependencias.
        if (!zonas.length) return undefined;

        let vigente = true;
        setAreasCargando(true);

        (async () => {
            const { data } = await RNHAreaGet(credentials, {
                zonaCod: codDeZona(zonas, zonaId),
                areaDisponible: "Y",
                // En la modificacion viaja tambien el hospital: el back devuelve las areas
                // libres MAS la que este hospital ya tiene asignada, que por definicion no
                // esta libre. Sin esto el select abria vacio y guardar borraba el area.
                //
                // Antes se resolvia pidiendo TODAS las areas de la zona original y filtrando
                // solo al cambiar de zona; eso ofrecia areas ya ocupadas por otros hospitales.
                // En el alta no hay id, asi que el param no viaja y quedan solo las libres.
                hospitalId,
            });

            if (!vigente) return;
            setAreas(data ?? []);
            setAreasCargando(false);
        })();

        return () => {
            vigente = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.zonaId, zonas]);

    // Las cabeceras que la zona elegida admite. Vacio mientras no haya zona.
    const cabecerasPosibles = cabecerasDeLaZona(zonas, values.zonaId, hospitalId);

    /**
     * Cambiar de zona invalida el area, que era de la zona anterior, y con ella la cabecera.
     *
     * Se limpia SIEMPRE, sin mirar cual estaba: las dos opciones son cabecera de un area
     * (`CABECERAS_CON_AREA`), y el area acaba de quedar vacia. Ademas la zona nueva puede
     * tener su cabecera tomada por otro hospital, con lo cual "Zona y Area" tampoco
     * sobreviviria. Dejarla puesta mostraria un valor que ya no figura entre las opciones.
     */
    const alCambiarZona = (evento) => {
        setFieldValue("zonaId", evento.target.value);
        setFieldValue("areaId", "");
        setFieldValue("cabecera", "");
    };

    return (
        <>
            <CamposIdentificacion />

            <FormikSelectInput
                className={estilos.col8}
                name="nivelId"
                labelText="Nivel de Complejidad"
                disabled={!niveles.length}
            >
                <option value="">
                    {niveles.length ? "Seleccione..." : "Cargando..."}
                </option>
                {/* El nombre y no el cod: es lo que se muestra en la grilla y en la
                    consulta, y elegir de una lista de codigos obliga a saberselos. */}
                {niveles.map(({ rnhNivelId, rnhNivelNombre, rnhNivelCod }) => (
                    <option key={rnhNivelId} value={rnhNivelId}>
                        {rnhNivelNombre ?? rnhNivelCod}
                    </option>
                ))}
            </FormikSelectInput>

            <FormikSelectInput
                className={estilos.col4}
                name="zonaId"
                labelText="Zona Sanitaria"
                disabled={!zonas.length}
                onChange={alCambiarZona}
            >
                <option value="">
                    {zonas.length ? "Seleccione..." : "Cargando..."}
                </option>
                {zonas.map(({ rnhZonaId, rnhZonaNombre }) => (
                    <option key={rnhZonaId} value={rnhZonaId}>
                        {rnhZonaNombre}
                    </option>
                ))}
            </FormikSelectInput>

            <FormikSelectInput
                className={estilos.col8}
                name="areaId"
                labelText="Área Programa"
                disabled={!values.zonaId || areasCargando}
            >
                <option value="">
                    {!values.zonaId
                        ? "Elegí una zona"
                        : areasCargando
                          ? "Cargando..."
                          : "Seleccione..."}
                </option>
                {areas.map(({ rnhAreaId, rnhAreaNombre }) => (
                    <option key={rnhAreaId} value={rnhAreaId}>
                        {rnhAreaNombre}
                    </option>
                ))}
            </FormikSelectInput>

            <FormikSelectInput
                className={estilos.col4}
                name="cabecera"
                labelText="Cabecera de"
                disabled={!values.zonaId}
            >
                <option value="" disabled>
                    {values.zonaId ? "Seleccione..." : "Elegí una zona..."}
                </option>
                {/* Solo las que admite la zona elegida: la lista cambia con `zonaId`. */}
                {CABECERA_OPCIONES.filter(({ value }) =>
                    cabecerasPosibles.includes(value)
                ).map(({ value, nombre }) => (
                    <option
                        key={value}
                        value={value}
                        // Ser cabecera de area no se puede afirmar antes de decir de cual.
                        disabled={
                            CABECERAS_CON_AREA.includes(value) && !values.areaId
                        }
                    >
                        {nombre}
                    </option>
                ))}
            </FormikSelectInput>
        </>
    );
}
